import Input from "sap/m/Input";
import { MetadataOptions } from "sap/ui/core/Element";
import { TranslationDto } from "../models/TranslationDto";
import {
  GetAvailableLanguages,
  GetCurrentLanguage,
} from "../utils/LanguageUtil";
import Label from "sap/m/Label";
import Popover from "sap/m/Popover";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import Button from "sap/m/Button";
import { PlacementType } from "sap/m/library";
import InputRenderer from "sap/m/InputRenderer";

/**
 * @namespace akademika.akamont.customControls
 */
export default class TranslationInput extends Input {
  private _popoverInputs: Input[];
  private _translationsPopover: Popover;

  static readonly metadata: MetadataOptions = {
    properties: {
      translationData: { type: "object[]" }, // array of objects of type { language: string, value: string }
    },
  };

  setTranslationData(translationData: TranslationDto[]) {
    this.setProperty("translationData", translationData, true);
    this.setValue("");

    if (!translationData) return;

    // set input value to translation for current language
    const currentLang = GetCurrentLanguage();
    const matchingTranslation = translationData.find(
      (td) => td.language === currentLang
    );
    if (matchingTranslation) {
      this.setValue(matchingTranslation.value);
    }
  }

  init(): void {
    super.init(); // TBD if works
    this.setShowValueHelp(true);
    this.setValueHelpIconSrc("sap-icon://translate");
    this.attachValueHelpRequest(this.showAllTranslationsPopup);
    this.attachLiveChange(this.onLiveChange);
  }

  static renderer = InputRenderer;

  updateTranslationText(langCode: string, value: string) {
    const transData: TranslationDto[] =
      this.getProperty("translationData") || [];

    let matchingTranslation = transData.find((td) => td.language === langCode);
    if (matchingTranslation) {
      matchingTranslation.value = value;
    } else {
      transData.push({ language: langCode, value: value });
    }

    this.setProperty("translationData", transData); // TBD if should add true
    if (GetCurrentLanguage() === langCode) {
      this.setValue(value);
    }

    // TBD when this block is needed
    if (this._popoverInputs) {
      const matchingPopoverInput = this._popoverInputs.find(
        (i) => i.data("langCode") === langCode
      );
      if (!matchingPopoverInput) throw "Missing popover input";

      matchingPopoverInput.setValue(value);
    }
  }

  onLiveChange() {
    const currentLang = GetCurrentLanguage();
    this.updateTranslationText(currentLang, this.getValue());
  }

  showAllTranslationsPopup() {
    if (!this._translationsPopover) {
      let formContent = [];
      this._popoverInputs = [];

      for (let lang of GetAvailableLanguages()) {
        const newInput = new Input({ required: true });
        newInput.attachLiveChange(() =>
          this.updateTranslationText(lang.code, newInput.getValue())
        );
        newInput.onsapenter = () => this._translationsPopover.close();

        formContent.push(
          new Label({
            text: lang.name,
            required: true,
          })
        );
        formContent.push(newInput);

        newInput.data("langCode", lang.code);
        this._popoverInputs.push(newInput);
      }

      this._translationsPopover = new Popover({
        placement: PlacementType.Bottom,
        title: "{i18n>enterTranslations}",
        contentWidth: "50rem",
        content: new SimpleForm({
          editable: true,
          layout: "ResponsiveGridLayout",
          content: formContent,
        }),
        endButton: new Button({
          text: "{i18n>close}",
          press: () => this._translationsPopover.close(),
        }),
      });
      this.addDependent(this._translationsPopover); // this is necessary for translations to work
    }

    const transData: TranslationDto[] = this.getProperty("translationData");
    for (const input of this._popoverInputs) {
      input.setValue("");
      if (transData) {
        const matchingTranslation = transData.find(
          (td) => td.language === input.data("langCode")
        );
        if (matchingTranslation) {
          input.setValue(matchingTranslation.value);
        }
      }
    }

    this._translationsPopover.openBy(this);
  }
}
